// Shared request helpers for the feed clients. Feeds are best-effort: every failure is a
// rejection the caller may swallow, never a half-parsed result.

import type { Fetcher } from '../types.ts';

const TIMEOUT_MS = 12000;

export const browserFetch: Fetcher = (url) => fetch(url);

/** Reject after TIMEOUT_MS so one hung provider cannot stall a refresh. */
function withTimeout<T>(work: Promise<T>, url: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${url} timed out`)), TIMEOUT_MS);
  });
  return Promise.race([work, late]).finally(() => clearTimeout(timer));
}

async function ok(fetcher: Fetcher, url: string): Promise<Response> {
  const res = await withTimeout(fetcher(url), url);
  if (!res.ok) {
    throw new Error(`${url} answered HTTP ${res.status}`);
  }
  return res;
}

export async function getJson<T>(fetcher: Fetcher, url: string): Promise<T> {
  return (await withTimeout(ok(fetcher, url).then((r) => r.json()), url)) as T;
}

export async function getText(fetcher: Fetcher, url: string): Promise<string> {
  return withTimeout(ok(fetcher, url).then((r) => r.text()), url);
}
